import { isEqual } from 'lodash'
import { createContext, type ReactNode, use, useCallback, useMemo, useRef, useState } from 'react'
import { type NavigateOptions, type Path, useLocation, useNavigate } from 'react-router'
import { combineRoutes, type composeRoute } from './lib'

type ComposedRoute = ReturnType<typeof composeRoute>
export type ComposedRoutes = readonly ComposedRoute[]
export type Route<TAppRoutes extends ComposedRoutes> = TAppRoutes[number]
export type RouteView<TAppRoutes extends ComposedRoutes> = TAppRoutes[number]['id']
export type RenderedRoutes<TAppRoutes extends ComposedRoutes> = ReturnType<TAppRoutes[number]['make']>
export type InternalAppNavigation<TAppRoutes extends ComposedRoutes> = ReturnType<typeof composeInternalAppNavigation<TAppRoutes>>

export const composeAppNavigation = <TAppRoutes extends ComposedRoutes>(appRoutes: TAppRoutes) => {
  const NavigationContext = composeInternalNavigationContext(appRoutes)

  return {
    useAppNavigation: () => use(NavigationContext),
    AppNavigationContext: ({ children }: { children: ReactNode }) => {
      const internalAppNavigationValue = composeInternalAppNavigation(appRoutes)
      return <NavigationContext.Provider value={internalAppNavigationValue}>{children}</NavigationContext.Provider>
    },
  }
}

export const composeInternalNavigationContext = <TAppRoutes extends ComposedRoutes>(appRoutes: TAppRoutes) => {
  const context = createContext<InternalAppNavigation<TAppRoutes>>({
    location: { state: 'error', key: 'none', ...window.location },
    appLocation: undefined,
    history: [],
    routes: combineRoutes(appRoutes),
    navigate: () => {},
    inView: () => false,
  })
  context.displayName = 'Navigation Context'
  return context
}

export const composeInternalAppNavigation = <TAppRoutes extends ComposedRoutes>(appRoutes: TAppRoutes) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [history, setHistory] = useState<RenderedRoutes<TAppRoutes>[]>([])
  const lastLocation = useRef<{
    to: RenderedRoutes<TAppRoutes>
    options?: NavigateOptions
  }>(undefined)

  const navigateRef = useRef(navigate)
  navigateRef.current = navigate

  const navigateCb = useCallback((to?: RenderedRoutes<TAppRoutes>, options?: NavigateOptions & { path?: Partial<Path> }): void | Promise<void> => {
    if (!to) {
      return
    }

    // reject multiple navigation requests to the same location
    if (lastLocation.current?.to.result === to.result && isEqual(lastLocation.current?.options, options)) {
      // poor partial checking if that URL is being reached
      if (window.location.toString().includes(to.result)) {
        return
      }
    }
    lastLocation.current = { to, options }
    setHistory((history) => [...history, to])
    if (options?.path) {
      return navigateRef.current({ ...options.path, pathname: to.result }, options)
    }
    return navigateRef.current(to.result, options)
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: this function is implicitly impacted by location changes somehow
  const inView = useCallback(
    (...args: RouteView<TAppRoutes>[]) => {
      const location = lastLocation.current?.to
      if (!location) {
        return false
      }
      for (const arg of args) {
        if (location.id === arg) {
          return true
        }
      }
      return false
    },
    [location],
  )

  // hint: restoring the context could be done by going through all known routes and match them against the location

  return {
    location,
    appLocation: useMemo(() => history?.at(-1), [history]),
    history,
    routes: combineRoutes(appRoutes),
    // forward navigation is just the result of all functions
    navigate: navigateCb,
    // impl notes: compare what is currently on top of the stack -> if it's the same, then go to a location until it's not the same view anymore
    // I don't like back. It's not good.
    // Back means, go one step back, but backing out of a view is so complicated with multiple parallel views due to the tabbing nature
    // if you want to go back, then just check yourself in you're in a view that you don't like and go back to your screens default view.
    // the screens view then itself is responsible for scrolling and whatever.
    // back: (from: AppView) => {},

    inView,
  }
}
